import React, { useContext } from "react";
import { graphql } from "gatsby";
import { LayoutContainer } from "../containers/layout";
import PageHeadMeta from "../components/global/page-head-meta";
import { Chatbot, CustomerLogos, GetStarted, LogoCarousel, QuoteCarousel, SectionWrapper } from "../components/sections";
import PortableText from "../components/portableText";
import { QuoteCardContent } from "../components/sections/quote-panels";
import { EyebrowPill } from "../components/serializers";
import { ImageBuilder } from "../components/global/image-builder";
import { ImageMagnifier } from "../components/global/image-magnifier";
import { ExternalVideo } from "../components/serializers-video";
import { CardContext } from "../components/card/context";
import { SectionContext } from "../components/sections/context";
import { pageDocPath } from "../../helpers";
import { typesMap } from "../components/sections/resources-list";
import { ArrowRight } from "../components/icon/arrow-left";
import { DARK_BACKGROUNDS } from "../components/global/layout";
import { cn, mapEdgesToNodes, toPlainText } from "../lib/helpers";
import { Expandable } from "../components/expandable";
import { CarouselSlides } from "../components/sections/carousel-slides";
import { CardGrid } from "../components/card/card-grid";
import { SiteContext } from "../components/global/site-context";
import { Button } from "../components/button";
import { ATTRIBUTION_PARAM, DEMO_PHRASE, DEMO_URL } from "../components/sections/snowflake/navigation";
import { VideoPopover } from "../components/video-popover";
import { PRODUCT_VIDEO_ID } from "../components/sections/snowflake/hero";
import { PillHeader, SectionHeader, SectionHeaders } from "../components/sections/wrapper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { EmailSignup } from "../components/global/email-signup";

import * as styles from "../components/portableText.module.css";
import { DEFAULT_COPILOT, DEFAULT_WORKFLOW } from "./agent";

export const query = graphql`
  query OfferingTemplateQuery($id: String!, $categories: [String!]) {
    doc: sanityOffering(id: { eq: $id }) {
      _type
      id
      _id
      title
      slug {
        current
      }
      _rawExcerpt(resolveReferences: { maxDepth: 5 })
      label
      header
      status
      heroImage {
        ...SanityImage
      }
      heroVideo {
        ...externalVideo
      }
      mainImage {
        ...SanityImage
      }
      quote {
        ... on SanityQuotable {
          ...Quotable
        }
        ... on SanityStory {
          ...cardNodeStory
        }
      }
      quotes {
        ... on SanityQuotable {
          ...Quotable
        }
        ... on SanityStory {
          ...cardNodeStory
        }
      }
      showSecurity
      promo {
        asset {
          id
          _type
          title
          _rawExcerpt(resolveReferences: { maxDepth: 5 })
          file {
            asset {
              url
            }
          }
          mainImage {
            ...SanityImage
          }
          slug {
            current
          }
        }
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
      }
      _rawGetStartedSummary(resolveReferences: { maxDepth: 5 })
      categories {
        _id
        title
        titlePublic
        slug {
          current
        }
        status
      }
      problems {
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
        mainImage {
          ...SanityImage
        }
      }
      _rawSolutionsIntro(resolveReferences: { maxDepth: 5 })
      solutions {
        label
        header
        _rawSummary(resolveReferences: { maxDepth: 5 })
        mainImage {
          ...SanityImage
        }
      }
      playbooks {
        ...cardNodePlaybook
      }
      backgroundColor
      chatbotFirstMessage
      chatbotReplies {
        text
        _rawPrompt(resolveReferences: { maxDepth: 5 })
        _rawAnswer(resolveReferences: { maxDepth: 5 })
      }
      replace {
        ... on SanityColumnsPortableText {
          _key
          header
          _rawContent(resolveReferences: { maxDepth: 5 })
          mainImage {
            ...SanityImageSimple
          }
          mainImageFade {
            ...SanityImageSimple
          }
          mainVideo {
            asset {
              url
            }
          }
          _rawContent(resolveReferences: { maxDepth: 5 })
          author {
            ...SanityAuthor
          }
          layout
          link
        }
      }
      faqs {
        question
        _rawAnswer(resolveReferences: { maxDepth: 5 })
        _key
      }
      # documentation
    }
    # logos: allSanityStory(
    #   limit: 6
    #   sort: { fields: [orderRank] }
    #   filter: {
    #     status: { eq: "published" }
    #     categories: {
    #       elemMatch: { _id: { in: $categories } }
    #     }
    #   }
    # ) {
    #   edges {
    #     node {
    #       ...cardNodeStory
    #     }
    #   }
    # }
    logos: allSanityCompany (
      limit: 60
      sort: { fields: [orderRank] }
      filter: {
        status: { eq: "published" }
        mainImageMono: { asset: { _id: { ne: null } } }
      }
    ) {
      edges {
        node {
          ...Company
        }
      }
    }
    recentTaggedPosts: allSanityPost(
      limit: 3
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { 
        slug: { current: { ne: null } },
        isPublished: { eq: true }
        categories: { elemMatch: { _id: { in: $categories } } }
    }) {
      edges {
        node {
          ...cardNode
        }
      }
    }
    recentPosts: allSanityPost(
      limit: 3
      sort: { fields: [publishedAt], order: [DESC] }
      filter: { 
        slug: { current: { ne: null } },
        isPublished: { eq: true }
    }) {
      edges {
        node {
          ...cardNode
        }
      }
    }
    chatAgent: sanityAgent(_id: { eq: "9984d4fd-97ce-4692-abba-3f2f6729ffdd" }) {
      ...Agent
    }
  }
`;

const OfferingTemplate = ({ data, pageContext }) => (
  <LayoutContainer
    doc={{
      ...data.doc,
      backgroundColor: data.doc.backgroundColor || "lightGreen",
      mode: pageContext.mode,
    }}
    hideNav={pageContext.mode === "hello"}
  >
    <PageHeadMeta title={data.doc.title || data.doc.header} doc={data.doc} />
    {data.doc &&
      <OfferingPage
        {...data.doc}
        pageContext={pageContext}
        posts={
          []
            .concat(mapEdgesToNodes(data.recentTaggedPosts))
            .concat(mapEdgesToNodes(data.recentPosts))
        }
        logos={mapEdgesToNodes(data.logos)}
        helloMode={pageContext.mode === "hello"}
        chatAgent={data.chatAgent}
      />
    }
  </LayoutContainer>
);

export default OfferingTemplate;

const BADGES = [
  {
    image: "https://cdn.sanity.io/images/vt347z5x/production/10a444f930aee29b1e389a7db86b1bd9b2a0ae56-370x370.png",
    text: "SOC2 compliance",
  },
  {
    image: "https://cdn.sanity.io/images/vt347z5x/production/ffd7ec37772694e549ee0b4f958dd2d04b104ecd-370x370.png",
    text: "GDPR compliance",
  },
  {
    image: "https://cdn.sanity.io/images/vt347z5x/production/c8b491bb4306ee07ab87a006f26ecfc6116715ff-371x370.png",
    text: "CCPA compliance",
  },
  {
    image: "https://cdn.sanity.io/images/vt347z5x/production/eaa753ded4174da8c4ee7572e9e43a2313b87dfe-370x370.png",
    text: <>SSO, SAML, <br /> and SCIM built in</>,
  },
  {
    image: "https://cdn.sanity.io/images/vt347z5x/production/600c103ea9dbc55a3b00095ff671659218a01298-370x370.png",
    text: <>Zero data retention <br /> for AI</>,
  },
];

const DEFAULT_FIRST_MESSAGE = "What GTM plays are you dreaming up?";

const OfferingPage = ({
  title,
  _rawExcerpt,
  header,
  heroImage,
  heroVideo,
  quote,
  quotes,
  categories,
  problems,
  _rawSolutionsIntro,
  solutions,
  backgroundColor,
  playbooks,
  label,
  replace,
  pageContext,
  posts,
  showSecurity,
  _rawGetStartedSummary,
  logos,
  helloMode,
  promo,
  faqs,
  chatbotFirstMessage,
  chatbotReplies,
  chatAgent,
}) => {
  const site = useContext(SiteContext);
  const displayQuote = quote ? quote?._type === "quotable" ? quote : quote.quote : null;
  const isDarkBg = DARK_BACKGROUNDS.includes(backgroundColor);
  const mainTag = categories?.[0];
  const categoryIds = categories?.map((c) => c._id) || [];
  const displayLogos = [...logos]
    .sort((a, b) => {
      const aMatch = a.categories?.some((c) => categoryIds.includes(c._id)) ? 0 : 1;
      const bMatch = b.categories?.some((c) => categoryIds.includes(c._id)) ? 0 : 1;
      return aMatch - bMatch;
    })
    .slice(0, 6);
  const tagTitle = ["published"].includes(mainTag?.status)
    ? (mainTag?.titlePublic || mainTag?.title).toLowerCase()
    : null;

  return (
    <>
      <SectionWrapper
        {...{
          noHeaders: true,
          name: "header",
        }}
      >
        <div className="grid sm:grid-cols-2 gap-12 md:gap-24 items-center">
          <div>
            <div
              className={cn(
                "mb-4 inline-block rounded-full font-medium text-dark-80 text-sm py-1 px-1.5 sm:px-2.5 md:px-3",
                site.isDarkBg
                  ? "text-black bg-light-70"
                  : "text-black bg-white",
              )}>
              {label}
            </div>
            <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight md:mr-20">{header}</h1>
            <PortableText
              blocks={_rawExcerpt}
              className="text-secondary leading-relaxed my-6"
            />
            {pageContext.mode === "hello" ? (
              <div className="flex">
                <VideoPopover
                  vimeoId={PRODUCT_VIDEO_ID}
                  buttonText={site.isNotMobile ? "Watch 5-min demo" : "Watch demo"}
                  buttonIcon={true}
                  buttonBg="bg-transparent"
                  buttonClass={cn("mr-3", isDarkBg ? "border-white text-white" : null)}
                />
                <Button
                  text={DEMO_PHRASE}
                  href={DEMO_URL}
                  bgColor={isDarkBg ? "bg-white text-black" : "bg-black"}
                  isBig={true}
                />
              </div>
            ) :
              <div>
                <Button
                  text={DEMO_PHRASE}
                  href={DEMO_URL}
                  bgColor={isDarkBg ? "bg-white text-black" : "bg-black"}
                />
              </div>}
          </div>
          <div className="sm:order-2">
            {heroVideo ? (
              <ExternalVideo node={heroVideo} inline={false} />
            ) : (
              <ImageBuilder image={heroImage} className="rounded-xl" />
            )}
          </div>
        </div>
      </SectionWrapper>

      {!isDarkBg && (
        <SectionWrapper
          container="full"
          padding={quotes.length > 0 ? null : "tight"}
          background="white"
          layout="centered"
        >
          <p className="text-xs text-center text-secondary">
            Teams running their outreach on Common Room
          </p>
          <LogoCarousel />
        </SectionWrapper>
      )}

      <a id="how-it-works"></a>

      {showSecurity && (
        <SectionWrapper header={site.token("offeringsSecurity")} background="white">
          <div className="flex gap-12 justify-between items-end mb-8">
            <h4 className="text-6xl">
              Enterprise-grade security by design
            </h4>
            <div>
              <a
                href="/security/"
                className="text-link hover:text-black whitespace-nowrap font-medium"
                target={helloMode ? "_blank" : null}
              >
                Read more <FontAwesomeIcon icon={faArrowRight} />
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 font-medium md:text-lg mb-2">
            {BADGES.map((badge, i) => (
              <div key={i} className="flex flex-col items-center bg-white p-2 pt-4 rounded-lg">
                <ImageBuilder src={badge.image} />
                <p className="text-center relative -top-4">{badge.text}</p>
              </div>
            ))}
          </div>
        </SectionWrapper>
      )}

      {problems.length > 0 && (
        <SectionWrapper background="black" header={site.token("offeringsProblems")}>
          <ul className={cn(
            "grid gap-4",
            problems.length == 2 ? "md:grid-cols-2" : "md:grid-cols-3"
          )}>
            {problems.map((p, index) => (
              <li
                key={index}
                className="p-8 md:p-4 md:py-6 bg-gray-900 border border-light-10 rounded-xl flex flex-col justify-between items-center"
              >
                <ImageBuilder image={p.mainImage} className="mb-4" />
                <div className="md:min-h-32">
                  <h3 className="font-medium mt-2 md:mt-0 text-2xl md:text-lg xl:text-xl mb-2">{p.header}</h3>
                  <PortableText blocks={p._rawSummary} className="text-auxiliary leading-snug" />
                </div>
              </li>
            ))}
          </ul>
        </SectionWrapper>
      )}

      {replace.length > 0 && (
        <SectionWrapper padding="top" header={site.token("offeringsTools")} background="white">
          <CarouselSlides section={{ content: replace, layout: "verticalTabs" }} />
        </SectionWrapper>
      )}

      <SectionWrapper background="white" padding="tight">
        <SolutionsSections
          intro={_rawSolutionsIntro}
          solutions={solutions}
        />
      </SectionWrapper>

      {/* <SectionContext.Provider value={{ isDarkBg: true }}>
        <SectionWrapper background="black"
          header={
            !playbooks && mainTag?.title
              ? `${site.token("playbooksPill")} for ${(
                mainTag?.titlePublic || mainTag?.title
              ).toLowerCase()}`
              : site.token("playbooksPill")}>
          <div>
            {playbooks ? (
              <CardGrid
                nodes={playbooks}
                maxColumns={3}
                browseMoreHref="/playbooks/"
                browseMoreText="View all playbooks"
                makeThumbnail={true}
                panels={true}
                cta={typesMap.get("playbook")?.cta}
                hideType={true}
                showSummary={true}
              />
            ) : (<PlaybooksBlock
              section={{ category: mainTag?.slug.current }}
            />)}
          </div>
        </SectionWrapper>
      </SectionContext.Provider > */}

      {promo?.asset && (
        <SectionWrapper>
          <a
            href={`/download/${promo?.asset?.slug?.current}/`}
            target="_blank"
            className="flex flex-col lg:flex-row items-center p-8 lg:p-12 py-24 lg:py-24 gap-8 lg:gap-24 mb-8 rounded-xl bg-cover bg-center"
            style={{
              backgroundImage: "url('https://cdn.sanity.io/images/vt347z5x/production/cc54aadfc518769b9de0dabb7972fa86926e3a18-2400x1040.png?auto=format')"
            }}
          >
            <ImageBuilder
              image={promo?.asset?.mainImage}
              width={400}
              className="rounded-xl border border-white shadow-lg transform transition-size scale-100 hover:scale-102 duration-200"
            />
            <div className="lg:order-first text-center lg:text-left sm:max-w-2/3">
              <h3 className="text-4xl">{promo?.header}</h3>
              <PortableText className="my-6" blocks={promo?._rawSummary} />
              <Button text="Download now for free" bgColor="bg-black" />
            </div>
          </a>
          {/* <Promo
          _rawOffer=""
          buttonText="Download"
          file=""
          externalUrl=""
          previewImage={promo.asset.mainImage.asset.url}
          formIntro={
            <div className="mt-2 mb-6">
              <h4 className="text-2xl font-medium mb-2">
                Master AI pipegen
              </h4>
              <p>Get the free enterprise guide</p>
            </div>
          }
        /> */}
        </SectionWrapper>
      )}

      {chatbotFirstMessage && (
        <div className="relative" id="chat">
          <SectionWrapper container="thin">
            <div className="">
              <h3 className="text-3xl font-semibold max-w-96 mb-2">Ask our GTM agent...</h3>
              {/* <EyebrowPill className="mb-2" isSpecial={true}>
                  Product specialist
                </EyebrowPill> */}
              <Chatbot
                // CMS default (from agent page)
                copilotID={DEFAULT_COPILOT}
                workflowID={DEFAULT_WORKFLOW}
                agent={{
                  ...chatAgent,
                  role: `Embed on website! Your page context is: ${title || header}`,
                  questions: chatbotReplies,
                }}
                firstMessage={chatbotFirstMessage || DEFAULT_FIRST_MESSAGE}
                textScroll={true}
                minimal={true}
                skipAutoFocus={true}
                stickyForm={true}
              />
            </div>
          </SectionWrapper>
        </div>
      )}

      {quotes.length > 0 ? (
        <SectionWrapper header={site.token("offeringsResults")}>
          <div className="md:grid md:grid-cols-3 gap-2 lg:gap-4 mb-4">
            {quotes.map((q, i) => (
              <QuoteLinkCard
                href={q._type === "story" && !helloMode ? pageDocPath(q) : null}
                quote={q?._type === "quotable" ? q : q.quote}
                isDarkBg={isDarkBg}
                key={i}
              />
            ))}
          </div>
          <SectionContext.Provider value={{ isDarkBg }}>
            <CustomerLogos
              nodes={displayLogos}
              section={displayLogos.length <= 0 ? { category: mainTag?.slug.current } : null}
            />
          </SectionContext.Provider>
        </SectionWrapper>
      ) : (
        <SectionWrapper container="large" layout="centered">
          <h3 className="font-semibold text-xl-homepage leading-none sm:leading-tight tracking-tight text-center mb-6 sm:mb-12">
            Real results from real teams
          </h3>
          <QuoteCarousel section={{ header: site.token("offeringsResults"), cards: 3, layout: "carousel" }} />
        </SectionWrapper>
      )}

      <SectionWrapper background="black" name="getStarted" container="thin" layout="centered">
        <h3 className="text-center mb-8 font-semibold text-xl-homepage leading-none sm:leading-tight tracking-tight">
          Learn how Common Room’s buyer intelligence informs our full revenue OS.
        </h3>
        <div className="text-center">
          <Button
            text="Read the blog"
            href="/blog/revenue-os/"
            bgColor="bg-transparent"
          />
        </div>
      </SectionWrapper>

      {faqs?.length > 0 && (
        <SectionWrapper container="thin" anchor="faqs" header="Common questions" layout="centered">
          <div className="max-w-screen-md">
            {faqs.map((faq, i) => (
              <Expandable
                key={faq._key || i}
                header={faq.question}
                answer={faq._rawAnswer ? toPlainText(faq._rawAnswer) : null}
              >
                <PortableText blocks={faq._rawAnswer} />
              </Expandable>
            ))}
          </div>
        </SectionWrapper>
      )}

      <SectionWrapper
        background="black"
        layout="centered"
      >
        <h3
          className="text-center mb-8 font-semibold text-xl-homepage"
        >
          Find your best buyers.
        </h3>
        <div className="max-w-md mx-auto">
          <EmailSignup
            toggle={(email) => {
              if (email) {
                window.location.href = `${DEMO_URL}?e=${btoa(email)}&${ATTRIBUTION_PARAM}=zra-two-step`;
              }
            }}
            buttonText={DEMO_PHRASE}
            supressMessage={true}
            placeholder="Work email"
            domId="demo-email"
            hubspotSubmit={true}
            inputClasses="py-3"
            attribution={{
              website_attribution: "demo-email",
              action: "demo-email",
              label: "email",
            }}
            wrapperClasses="border-2 border-light-10 xs:bg-light-5"
            buttonClasses="text-black"
          />
        </div>
      </SectionWrapper>

      {!helloMode && (
        <SectionWrapper
          background="black"
          anchor="resources"
        >
          <div>
            <PillHeader
              // isDarkBg={true}
              background="mint"
              header={
                tagTitle
                  ? `Learn more about ${tagTitle}`
                  : site.token("suggestions")
              }
              className="mb-6 lg:mb-10 bg-light-30"
            />
            {/* <ResourcesSuggestions section={{ cards: 3, types: ["post"] }} /> */}
            <CardGrid
              nodes={playbooks.slice(0, 1).concat(posts).slice(0, 3)}
              maxColumns={3}
              browseMoreHref={`/resources/${tagTitle ? categories[0].slug.current : ""}`}
              browseMoreText={
                tagTitle
                  ? `All ${tagTitle} resources`
                  : "View all resources"
              }
              makeThumbnail={true}
              panels={true}
              showSummary={true}
            />
          </div>
        </SectionWrapper>
      )}
    </>
  );
};


const QuoteLinkCard = ({ quote, isDarkBg, href }) => (
  <div>
    <a
      href={href}
      className={cn(
        "relative block rounded-lg p-4 lg:p-6 pt-0 mb-2 md:mb-0 h-full",
        isDarkBg ? "bg-light-5" : "bg-white",
        href ? "hover:shadow-lg" : null
      )}
    >
      <div>
        <CardContext.Provider value={{ authors: quote?.authors }}>
          <QuoteCardContent
            q={quote}
            showCardLogo={true}
            boldText={true}
          />
          {href && (
            <div className="mt-8">
              <span className="text-textLink font-medium">
                <ArrowRight className="mr-2" />
                {typesMap.get("story")?.cta}
              </span>
            </div>
          )}
        </CardContext.Provider>
      </div>
    </a>
  </div>
);

export const SolutionsSections = ({ intro, solutions, header, isDarkBg, container }) => (
  <>
    <PortableText blocks={intro} className="text-2xl my-16" />
    <ul className="flex flex-col max-w-screen-md lg:max-w-full">
      {solutions.map((s, index) => (
        <li
          key={index}
          className={cn(
            "mb-12 md:mb-20 overflow-hidden flex flex-col lg:flex-row lg:items-start gap-12 md:gap-24",
            "max-w-120 lg:max-w-full",
            isDarkBg ? "bg-light-5" : "bg-white",
            index % 2 === 0 ? null : "self-end lg:flex-row-reverse"
          )}
        >
          <div className="flex flex-col justify-center lg:flex-1 min-w-0 md:pt-4">
            <p className="text-sm uppercase mb-2 text-secondary tracking-wide">{s.label}</p>
            <h3 className="text-3xl xl:text-4xl font-semibold mb-6 max-w-3/4">{s.header}</h3>
            <PortableText
              blocks={s._rawSummary}
              className={cn(
                "text-secondary text-lg leading-normal",
                styles.arrowLinks
              )}
            />
          </div>
          <ImageMagnifier
            image={s.mainImage}
            width={552}
            className="rounded-xl"
          />
        </li>
      ))}
    </ul>
  </>
)